import React, { Suspense, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Provider } from 'react-redux';
import { store } from './store';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { UserProvider, useUser } from './contexts/UserContext';
import { SchoolProvider, useSchool } from './contexts/SchoolContext';
import languageService from './utils/languageService';
import { Toaster } from 'react-hot-toast';
import LoadingSpinner from './components/LoadingSpinner';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import UnifiedTranslationPage from './pages/UnifiedTranslationPage';
import TranslationQueuePage from './pages/TranslationQueuePage';
import MenuPreview from './pages/MenuPreview';
import MenuEditor from './pages/MenuEditor';
import SettingsPage from './pages/SettingsPage';
import PublicMenuViewer from './pages/PublicMenuViewer';
import LandingPage from './pages/LandingPage';
import SignupPage from './pages/SignupPage';
import SchoolSelectorDashboard from './pages/SchoolSelectorDashboard';
import { getLoginUrl } from './utils/auth';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import DashboardPage from './pages/DashboardPage';
import NotFound from './pages/NotFound';
import DishesPage from './pages/DishesPage';
import ProfilePage from './pages/ProfilePage';
import NutrientQueuePage from './pages/NutrientQueuePage';
import TagQueuePage from './pages/TagQueuePage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import PricingPage from './pages/PricingPage';
import { UrlProvider, useUrl } from './contexts/UrlContext';
import { getRecentSubdomainChange, clearSubdomainChangeData } from './utils/subdomainEvents';
import { setCurrentSubdomain } from './store/slices/subdomainSlice';
import AccessDeniedScreen from './components/AccessDeniedScreen';
import LoadingScreen from './components/LoadingScreen';
import { useUserSchools } from './hooks/useSchool';

// Protected route component
const ProtectedRoute = ({ element, redirectTo = '/login' }) => {
  const { school } = useSchool();
  const { t } = useTranslation();
  
  // Get the token from localStorage
  const token = localStorage.getItem('accessToken');
  
  // Convert redirectTo to a login URL if it's /login
  const loginUrl = redirectTo === '/login' ? getLoginUrl(school) : redirectTo;
  
  if (!token) {
    // Not logged in, redirect to login with message
    return <Navigate to={loginUrl} state={{ message: t('auth.pleaseLogin', 'Please log in to access this page') }} />;
  }
  
  // User is authenticated, render the protected component
  return element;
};

// This component checks if the user has the required role and redirects to login if not
const RouteWithRoleCheck = ({ element: Element, requiredRoles = [], ...rest }) => {
  const { loading: userLoading, user, updateUser, refreshUser } = useUser();
  const { isLoading: schoolsLoading, data: schools, refetch: refetchSchools } = useUserSchools();
  const isAuthenticated = !!user;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getBaseUrl } = useUrl();
  const location = useLocation();
  
  // Get the subdomain from URL using useParams()
  const params = useParams();
  const subdomain = params.subdomain;
  console.log("[RouteWithRoleCheck] Detected subdomain from useParams:", subdomain);
  const [checkedSubdomain, setCheckedSubdomain] = useState(subdomain);
  const [isProcessingSubdomainChange, setIsProcessingSubdomainChange] = useState(false);
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshTimeoutRef = useRef(null);
  
  // Force a refresh of school data when needed
  const forceRefreshUserSchools = useCallback(async () => {
    // Prevent multiple refreshes in quick succession
    if (isRefreshing) {
      console.log("[RouteWithRoleCheck] Already refreshing, skipping duplicate request");
      return;
    }
    
    console.log("[RouteWithRoleCheck] Forcing refresh of user schools data");
    setIsRefreshing(true);
    
    // Increment counter after a small delay to prevent rapid changes
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    refreshTimeoutRef.current = setTimeout(() => {
      setForceRefreshCounter(prev => prev + 1);
    }, 300);
    
    try {
      await refreshUser();
      await refetchSchools();
      console.log("[RouteWithRoleCheck] Refresh completed successfully");
    } catch (err) {
      console.error("[RouteWithRoleCheck] Error during forced refresh:", err);
    } finally {
      // Set a small delay before allowing another refresh
      setTimeout(() => {
        setIsRefreshing(false);
      }, 2000);
    }
  }, [refreshUser, refetchSchools, isRefreshing]);
  
  // Add this to detect recent subdomain changes
  useEffect(() => {
    // Skip if already processing a change
    if (isProcessingSubdomainChange) {
      return;
    }
    
    // Check if there was a recent subdomain change
    const recentChange = getRecentSubdomainChange();
    
    if (recentChange && subdomain) {
      console.log(`[RouteWithRoleCheck] Detected recent subdomain change from ${recentChange.previousSubdomain} to ${recentChange.newSubdomain}`);
      
      // If we're on the old subdomain URL, don't auto-redirect - just update Redux state
      if (subdomain === recentChange.previousSubdomain) {
        console.log(`[RouteWithRoleCheck] We're on the old subdomain (${recentChange.previousSubdomain}), updating state only`);
        
        // Update Redux store with new subdomain (but don't navigate)
        dispatch(setCurrentSubdomain(recentChange.newSubdomain));
        
        // Clear the subdomain change data to prevent future redirects
        clearSubdomainChangeData();
        return;
      }
      
      // If we're on the new subdomain, update Redux store and refresh user
      if (subdomain === recentChange.newSubdomain) {
        console.log(`[RouteWithRoleCheck] Updating context for new subdomain: ${recentChange.newSubdomain}`);
        setIsProcessingSubdomainChange(true);
        
        // Update Redux store with new subdomain
        dispatch(setCurrentSubdomain(recentChange.newSubdomain));
        
        // Refresh user data to get updated roles for the new subdomain
        forceRefreshUserSchools().then(() => {
          console.log('[RouteWithRoleCheck] User data refreshed after subdomain change');
          setIsProcessingSubdomainChange(false);
          clearSubdomainChangeData(); // Clear after handling
        }).catch(err => {
          console.error('[RouteWithRoleCheck] Error refreshing user after subdomain change:', err);
          setIsProcessingSubdomainChange(false);
        });
      }
    }
  }, [subdomain, location, dispatch, navigate, forceRefreshUserSchools, isProcessingSubdomainChange]);

  // Also watch for changes to the subdomain parameter
  useEffect(() => {
    // Skip if already processing a change
    if (isProcessingSubdomainChange || !subdomain) {
      return;
    }
    
    if (subdomain !== checkedSubdomain) {
      console.log(`[RouteWithRoleCheck] Subdomain changed from ${checkedSubdomain} to ${subdomain}`);
      setCheckedSubdomain(subdomain);
      
      // Update the subdomain in Redux
      dispatch(setCurrentSubdomain(subdomain));
      
      // Only refresh if it's not the initial render (checkedSubdomain was set before)
      if (checkedSubdomain) {
        // Force a user refresh to get updated roles for the new subdomain
        forceRefreshUserSchools().catch(err => {
          console.error('[RouteWithRoleCheck] Error refreshing user data after subdomain param change:', err);
        });
      }
    }
  }, [subdomain, checkedSubdomain, dispatch, forceRefreshUserSchools, isProcessingSubdomainChange]);

  // Force a refresh of schools data whenever the counter changes
  useEffect(() => {
    if (forceRefreshCounter > 0) {
      console.log(`[RouteWithRoleCheck] Force refresh triggered (${forceRefreshCounter})`);
      
      // Add debounce to prevent rapid consecutive refreshes
      const refreshTimer = setTimeout(() => {
        // Only refetch if not already loading
        if (!schoolsLoading) {
          refetchSchools();
        }
      }, 500);
      
      // Cleanup timer on unmount
      return () => clearTimeout(refreshTimer);
    }
  }, [forceRefreshCounter, refetchSchools, schoolsLoading]);

  // Don't do access checks while processing a subdomain change or while user data is loading
  if (isProcessingSubdomainChange) {
    return <LoadingScreen message={t('loadingRoles', 'Loading your access roles...')} />;
  }

  // Show loading screen if still loading user data
  if (userLoading || schoolsLoading) {
    return <LoadingScreen message={t('checkingAccess', 'Checking your access...')} />;
  }

  // If not authenticated, redirect to login with return path
  if (!isAuthenticated) {
    const loginUrl = getBaseUrl() + '/login?returnUrl=' + encodeURIComponent(window.location.pathname);
    return <Navigate to={loginUrl} replace />;
  }

  // If no subdomain is provided in URL, we can't check role access
  if (!subdomain && requiredRoles.length > 0) {
    console.log("[RouteWithRoleCheck] No subdomain found but roles required, redirecting to dashboard");
    return <Navigate to="/dashboard" replace />;
  }

  // If roles are required, check if user has at least one of the required roles for this subdomain
  if (requiredRoles.length > 0 && subdomain) {
    console.log(`[RouteWithRoleCheck] Checking roles for subdomain: ${subdomain}`);
    console.log(`[RouteWithRoleCheck] User schools data:`, schools);
    
    // Find the school matching the current subdomain
    const school = schools?.find(s => s.subdomain === subdomain);
    console.log(`[RouteWithRoleCheck] Found school:`, school);
    
    // Get user roles for this school
    const userRoles = school?.roles || [];
    console.log(`[RouteWithRoleCheck] User roles for this school:`, userRoles);
    console.log(`[RouteWithRoleCheck] Required roles for this route:`, requiredRoles);
    
    // Add more detailed debugging for newly created schools
    // Check if this is a newly created school
    const lastCreatedSchool = sessionStorage.getItem('lastCreatedSchool');
    if (lastCreatedSchool) {
      try {
        const schoolInfo = JSON.parse(lastCreatedSchool);
        const creationTime = schoolInfo.timestamp || 0;
        const timeSinceCreation = Date.now() - creationTime;
        
        // If school was created recently (within 5 minutes)
        if (timeSinceCreation < 300000 && schoolInfo.subdomain === subdomain) {
          console.log(`[RouteWithRoleCheck] This appears to be a newly created school:`, schoolInfo);
          console.log(`[RouteWithRoleCheck] Roles detected in created school data:`, schoolInfo.userRoles);
          console.log(`[RouteWithRoleCheck] Roles detected in current user data:`, userRoles);
          
          const userBeforeRedirect = sessionStorage.getItem('userBeforeRedirect');
          if (userBeforeRedirect) {
            try {
              const savedUserData = JSON.parse(userBeforeRedirect);
              console.log(`[RouteWithRoleCheck] User data before redirect:`, savedUserData);
              const savedSchool = savedUserData.schools?.find(s => s.id === schoolInfo.id);
              console.log(`[RouteWithRoleCheck] School in saved user data:`, savedSchool);
            } catch (e) {
              console.error(`[RouteWithRoleCheck] Failed to parse saved user data:`, e);
            }
          }
          
          // Check for admin access role in URL
          const isAdminAccess = window.location.pathname.includes('/admin');
          console.log(`[RouteWithRoleCheck] Is admin access path: ${isAdminAccess}`);
          
          // Check for settings path
          const isSettingsPath = window.location.pathname.includes('/settings');
          console.log(`[RouteWithRoleCheck] Is settings path: ${isSettingsPath}`);
          
          // Log roles needed for this part of the app
          if (isSettingsPath) {
            console.log(`[RouteWithRoleCheck] Settings path requires Admin or Superadmin roles`);
            console.log(`[RouteWithRoleCheck] User has Admin role:`, userRoles.includes('Admin'));
            console.log(`[RouteWithRoleCheck] User has Superadmin role:`, userRoles.includes('Superadmin'));
          }
        }
      } catch (e) {
        console.error(`[RouteWithRoleCheck] Error processing lastCreatedSchool:`, e);
      }
    }
    
    // Also check and log the schoolAccess from session storage
    const schoolAccess = sessionStorage.getItem('schoolAccess');
    if (schoolAccess) {
      try {
        const accessInfo = JSON.parse(schoolAccess);
        const accessTime = accessInfo.timestamp || 0;
        const timeSinceAccess = Date.now() - accessTime;
        
        // If this access occurred recently (within 1 minute)
        if (timeSinceAccess < 60000 && accessInfo.subdomain === subdomain) {
          console.log(`[RouteWithRoleCheck] Recent school access data found:`, accessInfo);
          console.log(`[RouteWithRoleCheck] Comparing roles - SessionStorage:`, accessInfo.userRoles, 'Current:', userRoles);
          
          // Check if roles are different
          const rolesMatch = 
            accessInfo.userRoles?.length === userRoles.length && 
            accessInfo.userRoles?.every(role => userRoles.includes(role));
          console.log(`[RouteWithRoleCheck] Roles match between storage and current: ${rolesMatch}`);
        }
      } catch (e) {
        console.error(`[RouteWithRoleCheck] Error processing schoolAccess:`, e);
      }
    }
    
    // Define role hierarchy (from highest to lowest)
    // Superadmin > Admin > Editor > Viewer
    const roleHierarchy = {
      'Superadmin': ['Superadmin', 'Admin', 'Editor', 'Viewer'],
      'Admin': ['Admin', 'Editor', 'Viewer'],
      'Editor': ['Editor', 'Viewer'],
      'Viewer': ['Viewer']
    };
    
    // Get all roles the user effectively has based on hierarchy
    const effectiveRoles = userRoles.reduce((allRoles, role) => {
      if (roleHierarchy[role]) {
        return [...allRoles, ...roleHierarchy[role]];
      }
      return [...allRoles, role];
    }, []);
    
    console.log(`[RouteWithRoleCheck] User effective roles after hierarchy:`, effectiveRoles);
    
    // Check if user has any of the required roles considering the hierarchy
    const hasRequiredRole = requiredRoles.some(role => effectiveRoles.includes(role));
    console.log(`[RouteWithRoleCheck] User has required role: ${hasRequiredRole}`);
    
    // If no matching school found or user doesn't have required roles
    if (!school) {
      console.log(`[RouteWithRoleCheck] No matching school found for subdomain ${subdomain}`);
      
      // If no schools data yet, try to refresh once
      if (!schools || schools.length === 0) {
        console.log(`[RouteWithRoleCheck] No schools data available, forcing a refresh`);
        forceRefreshUserSchools();
        return <LoadingScreen message={t('loadingSchools', 'Loading your schools...')} />;
      }
    }
    
    if (!hasRequiredRole) {
      // User doesn't have any of the required roles for this subdomain
      console.log(`[RouteWithRoleCheck] Access denied - user does not have required roles`);
      return <AccessDeniedScreen subdomain={subdomain} userRoles={userRoles} requiredRoles={requiredRoles} school={school} />;
    }
  }

  // User is authenticated and has required roles (if any), render the component
  console.log("[RouteWithRoleCheck] Access granted, rendering component:", Element);
  if (typeof Element === 'function') {
    return <Element params={params} />;
  } else if (React.isValidElement(Element)) {
    return Element;
  } else {
    console.error("[RouteWithRoleCheck] Invalid element prop:", Element);
    return <div>Error: Invalid component</div>;
  }
};

const HomePage = () => {
  const { t } = useTranslation();
  const { school, loading, isSubdomainRoute } = useSchool();
  
  console.log('HomePage - Current state:', { school, loading, isSubdomainRoute });
  
  // Show loading indicator while detecting the school
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4">{t('loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }
  
  // Check if the path contains "/login" to avoid rendering the public menu on login routes
  const isLoginPath = window.location.pathname.includes('/login');
  
  // If we're on a school subdomain AND not on a login path, show the public menu viewer
  if (school && !isLoginPath) {
    console.log('HomePage - Rendering PublicMenuViewer for school:', school.name);
    return <PublicMenuViewer />;
  }
  
  // If we're on the main domain, show the landing page
  if (!isSubdomainRoute) {
    console.log('HomePage - Rendering LandingPage (main domain)');
    return <LandingPage />;
  }
  
  // Fallback for cases where we have a subdomain route but no school found
  console.log('HomePage - School not found for subdomain route');
  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-4">
        <h1 className="text-2xl font-bold">{t('schoolNotFound', 'School Not Found')}</h1>
        <p className="mt-4">{t('schoolNotFoundMessage', 'The requested school could not be found.')}</p>
      </div>
    </>
  );
};

// Debug component existence to check for any missing/undefined components
console.log("Debug component imports: ", { 
  AdminDashboard, 
  MenuEditor, 
  UnifiedTranslationPage, 
  TranslationQueuePage, 
  MenuPreview, 
  SettingsPage, 
  DishesPage
});

// AppRoutes component that uses the school context
const AppRoutes = () => {
  const dispatch = useDispatch();
  const isAuthenticated = localStorage.getItem('accessToken') !== null;
  const { isSubdomainRoute, school } = useSchool();
  
  console.log('AppRoutes - isSubdomainRoute:', isSubdomainRoute, 'school:', school?.name || 'none');
  
  // Handle subdomain changes
  useEffect(() => {
    // Check for recent subdomain changes when the component mounts
    const recentChange = getRecentSubdomainChange();
    if (recentChange) {
      console.log(`[AppRoutes] Detected recent subdomain change from ${recentChange.previousSubdomain} to ${recentChange.newSubdomain}`);
      
      // Update Redux store with the new subdomain
      dispatch(setCurrentSubdomain(recentChange.newSubdomain));
      
      // Clear the subdomain change data to prevent duplicate handling
      clearSubdomainChangeData();
    }
  }, [dispatch]);
  
  // Load preferred language on app initialization
  useEffect(() => {
    const loadLanguage = async () => {
      try {
        await languageService.loadPreferredLanguage(dispatch, isAuthenticated);
        console.log('Language preference loaded successfully');
      } catch (error) {
        console.error('Failed to load language preference:', error);
      }
    };
    
    loadLanguage();
  }, [dispatch, isAuthenticated]);
  
  return (
    <Routes>
      {/* LOGIN ROUTES */}
      <Route path="/login" element={<LoginPage isMainDomain={true} />} />
      <Route path="/:subdomain/login" element={<LoginPage isMainDomain={false} />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/:subdomain/verify-email" element={<VerifyEmailPage />} />
      <Route path="/:subdomain/verify-email/:token" element={<VerifyEmailPage />} />
      
      {/* PASSWORD RESET ROUTES */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/:subdomain/reset-password" element={<ResetPasswordPage />} />
      <Route path="/:subdomain/reset-password/:token" element={<ResetPasswordPage />} />
      
      {/* PUBLIC PAGES */}
      <Route path="/terms" element={<TermsAndConditionsPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      
      {/* PROFILE ROUTES */}
      <Route path="/profile" element={<ProtectedRoute element={<ProfilePage />} />} />
      <Route path="/:subdomain/profile" element={<ProtectedRoute element={<ProfilePage />} />} />
      
      {/* MAIN DOMAIN DASHBOARD - For users to select which school to manage */}
      <Route path="/dashboard" element={
        <ProtectedRoute element={<SchoolSelectorDashboard />} />
      } />

      {/* SUBDOMAIN ADMIN ROUTES - All admin routes must have a subdomain */}
      <Route path="/:subdomain/admin" element={
        <RouteWithRoleCheck 
          element={AdminDashboard ? <AdminDashboard /> : <div>AdminDashboard not found</div>} 
          requiredRoles={["Viewer", "Editor", "Admin", "Superadmin"]}
        />
      } />
      <Route path="/:subdomain/admin/menu-editor" element={
        <RouteWithRoleCheck 
          element={MenuEditor ? <MenuEditor /> : <div>MenuEditor not found</div>} 
          requiredRoles={["Editor"]} 
        />
      } />
      <Route path="/:subdomain/admin/menu-editor/:menuId" element={
        <RouteWithRoleCheck 
          element={MenuEditor ? <MenuEditor /> : <div>MenuEditor not found</div>} 
          requiredRoles={["Editor"]} 
        />
      } />
      <Route path="/:subdomain/admin/translations" element={
        <RouteWithRoleCheck 
          element={UnifiedTranslationPage ? <UnifiedTranslationPage /> : <div>UnifiedTranslationPage not found</div>} 
          requiredRoles={["Editor"]} 
        />
      } />
      <Route path="/:subdomain/admin/translation-queue" element={
        <RouteWithRoleCheck 
          element={TranslationQueuePage ? <TranslationQueuePage /> : <div>TranslationQueuePage not found</div>} 
          requiredRoles={["Editor"]} 
        />
      } />
      <Route path="/:subdomain/admin/nutrient-queue" element={
        <RouteWithRoleCheck 
          element={NutrientQueuePage ? <NutrientQueuePage /> : <div>NutrientQueuePage not found</div>} 
          requiredRoles={["Editor"]} 
        />
      } />
      <Route path="/:subdomain/admin/tag-queue" element={
        <RouteWithRoleCheck 
          element={TagQueuePage ? <TagQueuePage /> : <div>TagQueuePage not found</div>} 
          requiredRoles={["Editor"]} 
        />
      } />
      <Route path="/:subdomain/admin/preview/:menuId" element={
        <RouteWithRoleCheck 
          element={MenuPreview ? <MenuPreview /> : <div>MenuPreview not found</div>} 
          requiredRoles={["Viewer"]} 
        />
      } />
      <Route path="/:subdomain/admin/preview" element={
        <RouteWithRoleCheck 
          element={MenuPreview ? <MenuPreview /> : <div>MenuPreview not found</div>} 
          requiredRoles={["Viewer"]} 
        />
      } />
      <Route path="/:subdomain/admin/settings" element={
        <RouteWithRoleCheck 
          element={SettingsPage ? <SettingsPage /> : <div>SettingsPage not found</div>} 
          requiredRoles={["Admin"]} 
        />
      } />

      {/* Legacy admin route redirects - redirect to dashboard to handle properly */}
      <Route path="/admin/*" element={
        <ProtectedRoute element={<Navigate to="/dashboard" replace />} />
      } />

      {/* Special name translation redirects */}
      <Route path="/:subdomain/admin/special-name-translations" element={
        <RouteWithRoleCheck 
          element={
            <Navigate to="../translations" state={{ initialTab: 'specialNames' }} replace />
          }
          requiredRoles={["Editor"]} 
        />
      } />
      
      {/* PUBLIC ROUTES */}
      {/* For development environment - path-based routes */}
      <Route path="/:subdomain/menu" element={<PublicMenuViewer />} />
      <Route path="/:subdomain" element={<HomePage />} />
      
      {/* HOME PAGE - will show landing or subdomain specific content */}
      <Route path="/" element={<HomePage />} />

      {/* New routes */}
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/:subdomain/admin/dishes" element={
        <RouteWithRoleCheck element={<DishesPage />} requiredRoles={["Editor"]} />
      } />
      <Route path="/terms" element={<TermsAndConditionsPage />} />

      {/* Catch-all 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <I18nextProvider i18n={i18n}>
        <UserProvider>
          <SchoolProvider>
            <UrlProvider>
            <Router>
              <Suspense fallback={<LoadingSpinner />}>
                <AppRoutes />
                <Toaster position="top-right" toastOptions={{
                  duration: 4000,
                  style: {
                    borderRadius: '8px',
                    background: '#fff',
                    color: '#333',
                  },
                }} />
              </Suspense>
            </Router>
            </UrlProvider>
          </SchoolProvider>
        </UserProvider>
      </I18nextProvider>
    </Provider>
  );
};

export default App; 